import React, { useState, useEffect, useMemo } from 'react';
import { StyleSheet, View, Pressable, Platform } from 'react-native';
import { usePathname } from 'expo-router';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import { ThemeNames, ACCENT_COLORS, ACCENT_TEXT_COLORS, Themes } from '@/constants/Colors';
import useResponsiveness from '@/hooks/useResponsiveness';
import NavigationBarButton from '@/components/Navigation/NavigationBarButton';
import { useTheme } from '@/context/ThemeContext';
import DropdownIcon from '@/components/DropdownMenu/DropdownIcon';
import Tabs from '@/components/Tabs';

// Create styles outside component
const createStyles = (colors, isXS) => StyleSheet.create({
  colorGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    padding: 8,
    justifyContent: 'flex-start',
    maxWidth: '100%',
  },
  colorOption: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      },
    }),
  },
  colorOptionActive: {
    borderWidth: 2,
    borderColor: colors.text_color_primary,
  },
  colorWrapper: {
    alignItems: 'center',
    flexBasis: '17%',
    flexGrow: 0,
    flexShrink: 0,
  },
  sectionTitle: {
    paddingHorizontal: 8,
  },
  exampleButtonContainer: {
    paddingHorizontal: 8,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: colors.border_color_secondary,
    marginTop: 16,
  },
  exampleButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tabsContainer: {
    paddingTop: Platform.OS === 'macos' ? 2 : 0,
    paddingBottom: Platform.OS === 'macos' ? 0 : 0,
    paddingHorizontal: Platform.OS === 'macos' ? 8 : 0,
    marginBottom: 8,
  }
});

/**
 * @param {object} props
 * @param {boolean} [props.showAccentSelector=true] — When false, only light/dark (etc.) theme is shown; accent colors are hidden.
 */
const ThemeSelector = ({ showAccentSelector = true }) => {
  const { colors, theme, setTheme, accentBackgroundColor, setAccentBackgroundColor, accentTextColor, setAccentTextColor } = useTheme();
  const { isXS } = useResponsiveness();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('theme');
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!showAccentSelector && activeTab === 'accents') {
      setActiveTab('theme');
    }
  }, [showAccentSelector, activeTab]);

  // Close dropdown when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  const styles = useMemo(() => createStyles(colors, isXS), [colors, isXS]);

  const tabsData = showAccentSelector
    ? [
        {
          id: 'theme',
          label: t('nav.themeSelector.theme'),
          active: activeTab === 'theme',
          onPress: () => setActiveTab('theme'),
        },
        {
          id: 'accents',
          label: t('nav.themeSelector.accents'),
          active: activeTab === 'accents',
          onPress: () => setActiveTab('accents'),
        },
      ]
    : [];

  return (
    <DropdownMenu.Root open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenu.Trigger asChild={Platform.OS === 'web' || Platform.OS === 'macos'}>
        <NavigationBarButton 
          icon='brightness'
          title={t('nav.themeSelector.theme')}
          tooltip={t('nav.themeSelector.theme')}
          hoverBackgroundColor={colors.solid_color_tertiary}
          isActive={isOpen}
          isRound={true}
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content className="DropdownMenuContent" style={{ minWidth: 200, maxWidth: 200 }}>
        {showAccentSelector ? (
          <View style={styles.tabsContainer}>
            <Tabs tabs={tabsData} size="s" />
          </View>
        ) : null}

        {(showAccentSelector ? activeTab === 'theme' : true) && ThemeNames.map((themeName) => {
          return (
            <DropdownMenu.CheckboxItem 
              key={themeName}
              className="DropdownMenuCheckboxItem"
              onValueChange={() => setTheme(themeName)}
              value={theme === themeName ? 'on' : 'off'}
            >
              <DropdownMenu.ItemIndicator className="DropdownMenuItemIndicator">
                <DropdownIcon isActive={theme === themeName} />
              </DropdownMenu.ItemIndicator>
              <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">
                {t(`themes.${themeName}`)}
              </DropdownMenu.ItemTitle>
              <View style={{
                width: 16,
                height: 16,
                borderRadius: 8,
                borderWidth: 1,
                borderColor: colors.border_color_secondary,
                marginLeft: 'auto',
                overflow: 'hidden',
              }}>
                <View style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '200%',
                  height: '200%',
                  backgroundColor: Themes[themeName].solid_color_block,
                  transform: [{ rotate: '45deg' }, { translateX: -8 }, { translateY: -12 }]
                }} />
                <View style={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  width: '200%',
                  height: '200%',
                  backgroundColor: Themes[themeName].solid_color_tertiary,
                  transform: [{ rotate: '45deg' }, { translateX: 8 }, { translateY: 12 }]
                }} />
              </View>
            </DropdownMenu.CheckboxItem>
          );
        })}

        {showAccentSelector && activeTab === 'accents' && (
          <>
            <View>
              <Text size='s' color='secondary' style={[styles.sectionTitle, { marginBottom: 8 }]}>
                {t('nav.themeSelector.accentColor')}
                {accentBackgroundColor && (
                  <Text color='tertiary' style={{ marginLeft: 4, textTransform: 'capitalize' }}>
                    • {accentBackgroundColor}
                  </Text>
                )}
              </Text>
              <View style={styles.colorGrid}>
                {ACCENT_COLORS.map(({ id, color }) => (
                  <View key={id} style={styles.colorWrapper}>
                    <Pressable
                      onPress={() => setAccentBackgroundColor(id.toLowerCase())}
                      style={[
                        styles.colorOption,
                        accentBackgroundColor === id && styles.colorOptionActive,
                        { backgroundColor: color },
                        id === 'multicolor' && { backgroundColor: colors.accent_color_primary },
                        id === 'white' && { borderWidth: 1, borderColor: colors.border_color_secondary }
                      ]}
                    />
                  </View>
                ))}
              </View>
            </View>

            <View style={{ marginTop: 16 }}>
              <Text size='s' color='secondary' style={[styles.sectionTitle, { marginBottom: 8 }]}>
                {t('nav.themeSelector.textColor')}
                {accentTextColor && (
                  <Text color='tertiary' style={{ marginLeft: 4, textTransform: 'capitalize' }}>
                    • {accentTextColor}
                  </Text>
                )}
              </Text>
              <View style={styles.colorGrid}>
                {ACCENT_TEXT_COLORS.map(({ id, color }) => (
                  <View key={id} style={styles.colorWrapper}>
                    <Pressable
                      onPress={() => setAccentTextColor(id.toLowerCase())}
                      style={[
                        styles.colorOption,
                        accentTextColor === id && styles.colorOptionActive,
                        { backgroundColor: color },
                        (id === 'white' || id === 'beige' || id === 'cream') && { 
                          borderWidth: 1,
                          borderColor: colors.border_color_secondary 
                        }
                      ]}
                    />
                  </View>
                ))}
              </View>
            </View>

            <View style={styles.exampleButtonContainer}>
              <View 
                style={[
                  styles.exampleButton,
                  { 
                    backgroundColor: accentBackgroundColor === 'multicolor' 
                      ? colors.accent_color_primary 
                      : ACCENT_COLORS.find(c => c.id.toLowerCase() === accentBackgroundColor)?.color || colors.accent_color_primary
                  }
                ]}
              >
                <Text
                  size='s' 
                  weight='medium'
                  style={
                  { 
                    color: ACCENT_TEXT_COLORS.find(c => c.id.toLowerCase() === accentTextColor)?.color || colors.accent_text_color
                  }
                }>
                  {t('nav.themeSelector.exampleButton')}
                </Text>
              </View>
            </View>
          </>
        )}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

ThemeSelector.displayName = 'ThemeSelector';

export default React.memo(ThemeSelector);
